import { createFileRoute, useNavigate } from '@tanstack/react-router'
import React, { useState, useCallback, useRef, useEffect } from 'react';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import {
  AlertTriangle,
  Play,
  CheckCircle,
  Eye,
  Download
} from 'lucide-react';
import { useUser } from '@clerk/clerk-react';

// Context and components
import { useApp } from '../contexts/AppContext';
import { analytics } from '../services/analytics';
import JobStatusPanel from '../components/JobStatusPanel';
import SubmitButton from '../components/SubmitButton';
import LocalFilePicker from '../components/LocalFilePicker';
import EmailInput from '../components/EmailInput';
import CategoryInput from '../components/CategoryInput';
import ViewResultsModal from '../components/ViewResultsModal';
import PurchaseCreditsModal from '../components/PurchaseCreditsModal';
import { useJobRestoration } from '../hooks/useJobRestoration';

const HomePage: React.FC = () => {
  const [showJobStatus, setShowJobStatus] = useState(false);
  const [showResultsModal, setShowResultsModal] = useState(false);
  const submitButtonContainerRef = useRef<HTMLDivElement>(null);
  // eslint-disable-next-line no-undef
  const submitButtonRef = useRef<HTMLButtonElement>(null);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  const search = Route.useSearch();

  // Get authenticated user from Clerk
  const { user, isSignedIn } = useUser();

  // Use enhanced app context
  const { state, setEmail, setCategories, restoreUserPreferences, setError, setHeicConversion, hidePurchaseModal, submitJob } = useApp();
  const { isEmailValid, areCategoriesValid, error, fileQueue, email, showPurchaseCreditsModal, purchaseModalData } = state;

  // Get user's email from Clerk (primary email address)
  const userEmail = (isSignedIn && user?.primaryEmailAddress?.emailAddress) || '';
  
  // Initialize job restoration on app load
  const { hasRestoredJob, restoredJob } = useJobRestoration({
    jobId: search.jobId,
    onJobRestored: () => {
      // Clear URL parameters when job is restored
      navigate({ to: '/', search: { jobId: undefined } });
    },
  });

  // Restore user preferences on component mount
  React.useEffect(() => {
    restoreUserPreferences();
    // Track page view
    analytics.page('SortingCat Home', {
      has_restored_job: hasRestoredJob,
      user_agent: navigator.userAgent
    });
  }, [restoreUserPreferences, hasRestoredJob]);

  // Show job status interface when URL contains jobId (for email links)
  // Supports both /?jobId=abc123 search param patterns
  useEffect(() => {
    const queryJobId = search.jobId;
    
    if (queryJobId) {
      setShowJobStatus(true);
    }
  }, [search.jobId]);


  // Handle email changes
  const handleEmailChange = useCallback((email: string, isValid: boolean) => {
    setEmail(email, isValid);
  }, [setEmail]);

  // Handle category changes
  const handleCategoriesChange = useCallback((categories: string[], mode: any, isValid: boolean) => {
    setCategories(categories, mode, isValid);
  }, [setCategories]);

  // Show restoration notification if job was restored
  React.useEffect(() => {
    if (hasRestoredJob && restoredJob) {
      console.log(`✅ Restored job ${restoredJob.id} with status: ${restoredJob.status}`);
    }
  }, [hasRestoredJob, restoredJob]);

  // Auto-populate email from Clerk when files are uploaded (if user is authenticated and no email set)
  useEffect(() => {
    if (fileQueue.length > 0 && !email && userEmail && isSignedIn) {
      // Automatically set email from authenticated user
      setEmail(userEmail, true);
      console.log('✅ Auto-populated email from authenticated user:', userEmail);
    } else if (fileQueue.length > 0 && fileQueue.length <= 10 && !email && emailInputRef.current) {
      // Focus email input if not authenticated, no email entered, and 10 or fewer images
      emailInputRef.current.focus();
      emailInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [fileQueue.length, email, userEmail, isSignedIn, setEmail]);

  // Auto-scroll to submit button and focus it when email is valid and files are uploaded
  useEffect(() => {
    if (isEmailValid && fileQueue.length > 0 && submitButtonContainerRef.current) {
      submitButtonContainerRef.current.scrollIntoView({
        behavior: 'smooth',
        block: 'center'
      });
      // Focus the submit button after scrolling
      setTimeout(() => {
        submitButtonRef.current?.focus();
      }, 500);
    }
  }, [isEmailValid, fileQueue.length]);

  // Handle Enter key submission from email input
  useEffect(() => {
    const handleEmailEnterSubmit = async () => {
      // Only submit if we have files and email is valid
      if (fileQueue.length > 0 && isEmailValid) {
        // Ensure categories are set to predefined mode (default)
        // This treats categories as already selected to standard
        setCategories([], 'predefined', true);

        // Small delay to ensure state is updated, then submit
        setTimeout(async () => {
          try {
            await submitJob();
          } catch (error) {
            console.error('❌ Enter key submit failed:', error);
          }
        }, 50);
      }
    };

    window.addEventListener('emailEnterSubmit', handleEmailEnterSubmit);
    return () => window.removeEventListener('emailEnterSubmit', handleEmailEnterSubmit);
  }, [fileQueue.length, isEmailValid, setCategories, submitJob]);

  return (
    <DndProvider backend={HTML5Backend}>
      <div className="min-h-screen bg-gradient-to-br from-orange-50 to-orange-100 px-4 py-6 sm:px-6 lg:px-8">
        {/* Skip Link for Keyboard Navigation */}
        <a href="#main-content" className="skip-link">
          Skip to main content
        </a>
        
        <div className="max-w-7xl mx-auto">
          <main id="main-content" role="main" className="space-y-6 pb-6">
          {/* Header */}
          <header className="card" role="banner">
            <div className="p-3.5 sm:p-5">
              <div className="text-center">
                {/* Mascot Logo */}
                <div className="flex justify-center mb-4">
                  <img 
                    src="/sorting_cat_mascot.png" 
                    alt="SortingCat Mascot" 
                    className="h-22 sm:h-28 lg:h-32 w-auto object-contain"
                    onError={(e) => {
                      console.warn('Mascot image failed to load');
                      // Hide the image container gracefully if mascot fails to load
                      e.currentTarget.style.display = 'none';
                    }}
                  />
                </div>
                <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-3 leading-tight">
                  SortingCat
                </h1>
                <p className="text-lg sm:text-xl text-purple-600 font-medium mb-4">
                  AI-powered image organization & smart categorization
                </p>
                <p className="text-sm sm:text-base text-gray-600 mb-6">
                  Upload your pictures and our purrfect sorting wizard will describe and organize for you
                </p>
              </div>
              
              
              {/* Error Display */}
              {error && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
                  <div className="flex items-start">
                    <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5 mr-3 flex-shrink-0" />
                    <div className="flex-1">
                      <p className="text-sm font-medium text-red-800">Error:</p>
                      <p className="text-sm text-red-700">{error}</p>
                    </div>
                    <button
                      onClick={() => setError(null)}
                      className="ml-3 text-red-500 hover:text-red-700 min-h-[44px] min-w-[44px] flex items-center justify-center"
                      aria-label="Dismiss error"
                      type="button"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </div>
                </div>
              )}
              

            </div>
          </header>


          {/* Job Status Panel - appears above main content when active */}
          <JobStatusPanel />

          {/* Job Status Interface - shown above file selection when URL contains jobId */}
          {showJobStatus && (
            <section className="card" aria-label="Job status">
              <div className="p-4 border-b border-gray-200 text-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-blue-600" />
                  Job Status
                </h2>
              </div>
              <div className="p-4 sm:p-6">
                {!state.currentJob ? (
                  <div className="text-center">
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                      <span className="ml-3 text-gray-600">Loading job details...</span>
                    </div>
                    <p className="text-sm text-gray-500 mt-2">
                      Retrieving your job information from the link
                    </p>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="mb-4">
                      <div className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                        state.currentJob.status === 'completed' ? 'bg-green-100 text-green-800' :
                        state.currentJob.status === 'processing' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }`}>
                        {state.currentJob.status === 'completed' && <CheckCircle className="w-4 h-4 mr-1" />}
                        {state.currentJob.status.charAt(0).toUpperCase() + state.currentJob.status.slice(1)}
                      </div>
                    </div>
                    <p className="text-gray-700 mb-2">
                      <strong>Job ID:</strong> {state.currentJob.id}
                    </p>
                    <p className="text-gray-700 mb-4">
                      <strong>{state.currentJob.fileCount} files</strong> processed
                    </p>
                    
                    
                    <button
                      onClick={() => {
                        setShowJobStatus(false);
                        // Clear URL parameters using TanStack Router
                        navigate({ to: '/', search: { jobId: undefined } });
                      }}
                      className="btn-secondary text-sm"
                    >
                      ← Start New Job
                    </button>
                  </div>
                )}
              </div>
            </section>
          )}

          {/* Main Upload Interface - hidden when viewing job status from email links OR when job is active */}
          {!showJobStatus && !state.currentJob && (
            <>
              {/* File Selection */}
              <section className="card" aria-label="File selection">
                <LocalFilePicker />
              </section>

              {/* Email Input */}
              <section className="card" aria-label="Email input">
                <div className="p-4 sm:p-6">
                  <EmailInput
                    onEmailChange={handleEmailChange}
                    placeholder=""
                    required={true}
                    ref={emailInputRef}
                  />
                </div>
              </section>

              {/* HEIC Conversion Checkbox - appears only when HEIC files are detected */}
              {state.hasHeicFiles && (
                <section className="card" aria-label="HEIC conversion settings">
                  <div className="p-4 sm:p-6">
                    <label className="flex items-start space-x-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={state.convertHeicAndHeif}
                        onChange={(e) => {
                          setHeicConversion(e.target.checked);
                        }}
                        className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                      />
                      <div className="flex-1">
                        <div className="text-sm font-medium text-gray-900">
                          Convert HEIC/HEIF files to JPEG
                        </div>
                        <div className="text-xs text-gray-600 mt-1">
                          HEIC files will be converted to JPEG format for wider compatibility.
                          Uncheck to keep original HEIC format.
                        </div>
                      </div>
                    </label>
                  </div>
                </section>
              )}

              {/* Category Input */}
              <section className="card w-full" aria-label="Category selection">
                <div className="p-4 sm:p-6">
                  <CategoryInput
                    onCategoriesChange={handleCategoriesChange}
                    initialMode={state.categoryMode}
                    initialCategories={state.customCategories}
                    required={false}
                  />
                </div>
              </section>

              {/* Action Bar */}
              <div ref={submitButtonContainerRef} className="card">
                <div className="p-4 border-b border-gray-200 text-center mb-4">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center justify-center">
                    <Play className="w-5 h-5 mr-2" />
                    4) Submit
                  </h2>
                </div>
                <div className="p-4 flex flex-col sm:flex-row items-center gap-4 sm:justify-between">
                  <div className="text-center sm:text-left">
                    <span className="text-sm text-gray-600 block sm:inline">
                      Ready to process? Check your email and categories, then submit.
                    </span>
                  </div>

                  <div className="flex flex-col sm:flex-row items-center gap-3 w-full sm:w-auto">
                    <SubmitButton
                      ref={submitButtonRef}
                      disabled={!isEmailValid || !areCategoriesValid || fileQueue.length === 0}
                      className="w-full sm:w-auto"
                    />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Results Section - appears below submit when job is completed (but not when viewing from job URL) */}
          {state.currentJob && state.currentJob.status === 'completed' && !showJobStatus && (
            <div className="card animate-fadeIn">
              <div className="p-4 border-b border-gray-200 text-center mb-4">
                <h2 className="text-lg font-semibold text-gray-900 flex items-center justify-center">
                  <CheckCircle className="w-5 h-5 mr-2 text-green-600" />
                  Processing Complete!
                </h2>
              </div>
              <div className="p-4">
                <div className="text-center mb-6">
                  <p className="text-gray-700 mb-2">
                    <strong>Job ID:</strong> {state.currentJob.id}
                  </p>
                  <p className="text-gray-700 mb-4">
                    <strong>{state.currentJob.fileCount} files</strong> processed in{' '}
                    <strong>
                      {(() => {
                        const duration = state.currentJob.completedAt 
                          ? state.currentJob.completedAt.getTime() - state.currentJob.createdAt.getTime()
                          : 0;
                        const seconds = Math.floor(duration / 1000);
                        return seconds < 60 ? `${seconds}s` : `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
                      })()}
                    </strong>
                  </p>
                  <p className="text-sm text-gray-600 mb-6">
                    Your organized images are ready.
                  </p>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => {
                      console.log('View results for job:', state.currentJob?.id);
                      setShowResultsModal(true);
                    }}
                    className="btn-primary flex items-center justify-center space-x-2 hover:shadow-lg transition-shadow duration-200"
                  >
                    <Eye className="w-4 h-4" />
                    <span>View Results</span>
                  </button>
                  
                  <button
                    onClick={async () => {
                      if (state.currentJob) {
                        console.log('Download results for job:', state.currentJob.id);
                        // Use Convex HTTP endpoint (.convex.site for HTTP routes)
                        const convexUrl = process.env.REACT_APP_CONVEX_URL || 'https://upbeat-butterfly-447.convex.cloud';
                        const convexSiteUrl = convexUrl.replace('.convex.cloud', '.convex.site');
                        const downloadUrl = `${convexSiteUrl}/download?jobId=${state.currentJob.id}`;
                        const link = document.createElement('a');
                        link.href = downloadUrl;
                        link.download = `sortingcat-${state.currentJob.id}.zip`;
                        link.target = '_blank';
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                      }
                    }}
                    className="btn-secondary flex items-center justify-center space-x-2 hover:shadow-lg transition-shadow duration-200"
                  >
                    <Download className="w-4 h-4" />
                    <span>Download ZIP</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Footer */}
          <footer className="card" role="contentinfo">
            <div className="p-3 sm:p-4 bg-gray-50 border-t border-gray-200">
              <div className="text-xs text-gray-500 text-center">
                <div className="mt-2 flex justify-center items-center space-x-4">
                  <button 
                    onClick={() => navigate({ to: '/terms' })}
                    className="text-purple-600 hover:text-purple-800 font-medium cursor-pointer"
                  >
                    Terms
                  </button>
                  <button 
                    onClick={() => navigate({ to: '/privacy' })}
                    className="text-purple-600 hover:text-purple-800 font-medium cursor-pointer"
                  >
                    Privacy
                  </button>
                  <span className="text-gray-400">© arewe.ai, 2025</span>
                </div>
              </div>
            </div>
          </footer>
          </main>
        </div>
      </div>

      {/* Results Modal */}
      {state.currentJob && (
        <ViewResultsModal
          jobId={state.currentJob.id}
          isOpen={showResultsModal}
          onClose={() => setShowResultsModal(false)}
        />
      )}

      {/* Purchase Credits Modal */}
      <PurchaseCreditsModal
        isOpen={showPurchaseCreditsModal}
        onClose={hidePurchaseModal}
        fileCount={purchaseModalData?.fileCount}
        creditsNeeded={purchaseModalData?.creditsNeeded}
        totalSizeBytes={purchaseModalData?.totalSizeBytes}
      />
    </DndProvider>
  );
};

export const Route = createFileRoute('/')({
  component: HomePage,
  validateSearch: (search: Record<string, unknown>) => {
    return {
      jobId: search.jobId as string | undefined,
    }
  },
})